// Fluid Dynamics module (WASM-powered)
import init, {
  get_fluid_defaults, run_boundary_scan, run_pipeflow_scan,
  run_drag_scan, run_heattransfer_scan, run_compressible_scan,
  get_lbm_defaults, run_lbm_scan
} from './engines.js';
import { getLicense, hasFullAccess } from './auth.js';
import { GeoNum, geo } from './geo-num.js';
import { ZONES_FLUIDS } from './geo-num-zones.js';
import { registerReport } from './report-gen.js';

const _MODULE = 'Fluids';
const charts = {};
let ready = false, defaults = null, lbmDefaults = null;

// Reference correlations and experimental values for fluid dynamics
const FLUIDS_REFS = {
  blasius_friction: {
    formula: 'f = 0.3164 / Re^0.25',
    range: 'Re < 10\u2075',
    source: 'Blasius 1913',
    url: 'https://doi.org/10.1007/BF01721323'
  },
  colebrook_white: {
    formula: '1/\u221Af = -2 log\u2081\u2080(\u03B5/3.7D + 2.51/(Re\u221Af))',
    range: 'turbulent pipe flow',
    source: 'Colebrook 1939',
    url: 'https://doi.org/10.1680/ijoti.1939.13150'
  },
  sphere_drag: {
    value: 0.47, condition: 'Re \u2248 10\u2075',
    source: 'Schlichting, Boundary Layer Theory',
    url: 'https://doi.org/10.1007/978-3-642-85829-1'
  },
  flat_plate_cf: {
    formula: 'C_f = 0.664 / \u221ARe',
    range: 'laminar flat plate',
    source: 'Blasius solution',
    url: 'https://doi.org/10.1007/BF01721323'
  },
  dittus_boelter: {
    formula: 'Nu = 0.023 \u00B7 Re^0.8 \u00B7 Pr^n',
    note: 'n = 0.4 (heating), n = 0.3 (cooling)',
    source: 'Dittus-Boelter 1930',
    url: 'https://doi.org/10.1016/S0735-1933(99)00075-5'
  }
};

function fluidsRefLink(ref) {
  return `<a href="${ref.url}" target="_blank" style="color:#4fc3f7;text-decoration:none;">${ref.source}</a>`;
}

function renderBoundaryRefNote(containerId) {
  const el = document.getElementById(containerId);
  if (!el) return;
  const bl = FLUIDS_REFS.flat_plate_cf;
  el.innerHTML = `<div style="margin-top:10px;padding:8px 12px;background:#0b1520;border:1px solid #1e3a5f;border-radius:4px;font-size:11px;color:#94a3b8;line-height:1.7;">
    <strong style="color:#4fc3f7;">Reference Correlations</strong><br>
    Laminar flat plate C<sub>f</sub>: <strong>${bl.formula}</strong> (${fluidsRefLink(bl)})<br>
    Blasius friction factor: <strong>${FLUIDS_REFS.blasius_friction.formula}</strong> valid for ${FLUIDS_REFS.blasius_friction.range} (${fluidsRefLink(FLUIDS_REFS.blasius_friction)})
  </div>`;
}

function renderPipeflowRefNote(containerId) {
  const el = document.getElementById(containerId);
  if (!el) return;
  const bl = FLUIDS_REFS.blasius_friction;
  const cw = FLUIDS_REFS.colebrook_white;
  el.innerHTML = `<div style="margin-top:10px;padding:8px 12px;background:#0b1520;border:1px solid #1e3a5f;border-radius:4px;font-size:11px;color:#94a3b8;line-height:1.7;">
    <strong style="color:#4fc3f7;">Reference Correlations</strong><br>
    Blasius (smooth, turbulent): <strong>${bl.formula}</strong> for ${bl.range} (${fluidsRefLink(bl)})<br>
    Colebrook-White (rough): <strong>${cw.formula}</strong> \u2014 ${cw.range} (${fluidsRefLink(cw)})
  </div>`;
}

function renderDragRefNote(containerId) {
  const el = document.getElementById(containerId);
  if (!el) return;
  const sd = FLUIDS_REFS.sphere_drag;
  el.innerHTML = `<div style="margin-top:10px;padding:8px 12px;background:#0b1520;border:1px solid #1e3a5f;border-radius:4px;font-size:11px;color:#94a3b8;line-height:1.7;">
    <strong style="color:#4fc3f7;">Reference Values</strong><br>
    Sphere C<sub>D</sub>: <strong>${sd.value}</strong> at ${sd.condition} (${fluidsRefLink(sd)})<br>
    Laminar flat plate C<sub>f</sub>: <strong>${FLUIDS_REFS.flat_plate_cf.formula}</strong> (${fluidsRefLink(FLUIDS_REFS.flat_plate_cf)})
  </div>`;
}

function renderHeatTransferRefNote(containerId) {
  const el = document.getElementById(containerId);
  if (!el) return;
  const db = FLUIDS_REFS.dittus_boelter;
  el.innerHTML = `<div style="margin-top:10px;padding:8px 12px;background:#0b1520;border:1px solid #1e3a5f;border-radius:4px;font-size:11px;color:#94a3b8;line-height:1.7;">
    <strong style="color:#4fc3f7;">Reference Correlations</strong><br>
    Dittus-Boelter: <strong>${db.formula}</strong><br>
    ${db.note} (${fluidsRefLink(db)})
  </div>`;
}

// Animation state
const animData = {};

export async function initWasm() {
  try {
    await init();
    ready = true;
    defaults = JSON.parse(get_fluid_defaults());
    lbmDefaults = JSON.parse(get_lbm_defaults());
    populateDefaults();
    // Configure GeoNum for fluids domain (micron to km scale)
    GeoNum.setZoneConfig(ZONES_FLUIDS.boundaries, ZONES_FLUIDS.minZone);
  }
  catch (err) { console.error('WASM init (fluids):', err); }
}

export function init_ui() {
  document.querySelectorAll('.fltab').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.fltab').forEach(b => b.classList.remove('active'));
      document.querySelectorAll('.fluids-panel').forEach(p => p.classList.remove('active'));
      btn.classList.add('active');
      document.getElementById('flp-' + btn.dataset.fl).classList.add('active');
    });
  });
  bind('btn-boundary', boundaryScan, 'Boundary Layer', 'Running boundary layer scan');
  bind('btn-pipeflow', pipeflowScan, 'Pipe Flow', 'Running pipe flow scan');
  bind('btn-drag', dragScan, 'Drag Analysis', 'Running drag scan');
  bind('btn-heattransfer', heattransferScan, 'Heat Transfer', 'Running heat transfer scan');
  bind('btn-compressible', compressibleScan, 'Compressible Flow', 'Running compressible flow scan');
  bind('btn-lbm', lbmScan, null, null); // lbmScan manages its own loading modal
}

export function applyFluidsGating() {
  const license = getLicense();
  const tier = license?.tier || 'trial';
  const has = hasFullAccess(tier) || license?.modules?.includes('fluids') || (tier === 'trial' && license?.isActive);
  const g = document.getElementById('fl-gate');
  const c = document.getElementById('fl-content');
  if (g && c) {
    if (has) { g.classList.add('hidden'); c.classList.remove('hidden'); }
    else { g.classList.remove('hidden'); c.classList.add('hidden'); }
  }
}

function populateDefaults() {
  if (!defaults) return;
  setTA('in-boundary', defaults.boundary?.configs);
  setTA('in-pipeflow', defaults.pipeflow?.pipes);
  setTA('in-drag', defaults.drag?.bodies);
  setTA('in-heattransfer', defaults.heattransfer?.configs);
  setTA('in-compressible', defaults.compressible?.configs);
  setTA('in-lbm', lbmDefaults?.configs);
}

function setTA(id, v) { const el = document.getElementById(id); if (el && v) el.value = JSON.stringify(v, null, 2); }
function bind(id, fn, loadTitle, loadText) {
  const el = document.getElementById(id);
  if (!el) return;
  el.addEventListener('click', async () => {
    if (!ready) return;
    el.disabled = true;
    // Show the shared spinner so the UI never freezes during the synchronous engine run.
    // When loadTitle is null the handler manages its own modal (e.g. lbmScan), so skip here.
    const useLoading = loadTitle && window.gdbsShowLoading;
    if (useLoading) {
      window.gdbsShowLoading(loadTitle, loadText || 'Computing...');
      // Yield one frame so the spinner paints before the blocking call.
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
    }
    try { await fn(); }
    finally {
      if (useLoading && window.gdbsHideLoading) window.gdbsHideLoading();
      el.disabled = false;
    }
  });
}
function status(id, m) { const el = document.getElementById('s-' + id); if (el) el.textContent = m; }
// Running modal - reuse the global #hpc-loading-modal spinner (same pattern as hpc-ligo.js / hpc-bssn.js)
function showLoading(title, text) {
  const modal = document.getElementById('hpc-loading-modal');
  const titleEl = document.getElementById('hpc-loading-title');
  const textEl = document.getElementById('hpc-loading-text');
  if (titleEl) titleEl.textContent = title;
  if (textEl) textEl.textContent = text;
  if (modal) modal.classList.remove('hidden');
}
function hideLoading() {
  const modal = document.getElementById('hpc-loading-modal');
  if (modal) modal.classList.add('hidden');
}
function fmtSci(v) { return typeof v === 'number' ? (Math.abs(v) > 1e6 || (Math.abs(v) < 0.001 && v !== 0) ? v.toExponential(3) : v.toFixed(4)) : v; }
function tierColor(val) {
  const v = parseFloat(val); let r, g, b;
  if (v < 0.5) { const t = v / 0.5; r = 220; g = Math.round(50 + 170 * t); b = 50; }
  else { const t = (v - 0.5) / 0.5; r = Math.round(220 - 180 * t); g = 200; b = Math.round(50 + 30 * t); }
  return `rgb(${r},${g},${b})`;
}
function renderGradientBar(tc) {
  if (!tc) return '';
  const base = [{ val: tc.core, name: 'Core' }, { val: tc.magnitude, name: 'Mag' }, { val: tc.phase, name: 'Phase' }, { val: tc.proportion, name: 'Prop' }];
  const rel = [];
  if (tc.relational) { for (let i = 0; i < tc.relational.length; i++) { const r = tc.relational[i], l = i + 2; rel.push({ val: r[0], name: `Mag\u00D7${l}` }, { val: r[1], name: `Phase\u00D7${l}` }, { val: r[2], name: `Prop\u00D7${l}` }); } }
  const all = [...base, ...rel], pct = (100 / all.length).toFixed(2);
  let h = '<div class="tc-wrap"><div class="tc-bar">';
  for (const t of all) h += `<div class="tc-seg" style="width:${pct}%;background:${tierColor(Math.max(0.08, t.val))}"><span class="tc-tip">${t.name}<br>${t.val.toFixed(3)}</span></div>`;
  h += '</div><div class="tc-labels">'; for (const t of all) h += `<span class="tc-lbl" style="width:${pct}%">${t.val.toFixed(2)}</span>`;
  return h + '</div></div>';
}
function renderTable(id, headers, rows) {
  let h = '<thead><tr>' + headers.map(x => '<th>' + x + '</th>').join('') + '</tr></thead><tbody>';
  for (const r of rows) h += '<tr>' + r.map(v => '<td>' + fmtSci(v) + '</td>').join('') + '</tr>';
  const el = document.getElementById('t-' + id); if (el) el.innerHTML = h + '</tbody>';
}
function makeChart(id, labels, datasets, xLbl, yLbl, type) {
  if (charts[id]) charts[id].destroy();
  const c = document.getElementById('c-' + id); if (!c) return;
  charts[id] = new Chart(c.getContext('2d'), { type: type || 'bar', data: { labels, datasets },
    options: { responsive: true, maintainAspectRatio: false, animation: false,
      scales: { x: { title: { display: true, text: xLbl } }, y: { title: { display: true, text: yLbl } } },
      plugins: { legend: { position: 'top' } } } });
  try { const _s=datasets.map(ds=>({label:ds.label??'Series',color:ds.borderColor??(typeof ds.backgroundColor==='string'?ds.backgroundColor:'#2563eb'),dashed:!!(ds.borderDash&&ds.borderDash.length),data:labels?labels.map((l,i)=>{const v=ds.data[i];return[typeof l==='number'?l:i,v!=null&&typeof v==='object'?(v.y??0):(v??0)];}):ds.data.map(p=>Array.isArray(p)?p:(p&&typeof p==='object'?[p.x??0,p.y??0]:[0,p??0]))}));registerReport({title:id,module:_MODULE,params:[],series:_s,xLabel:xLbl,yLabel:yLbl});} catch(_){}
}

// GeoNum precision demonstration for fluids calculations
function geoNumDemo(value, description) {
  const ieee = value;
  const geoVal = geo(value);
  const geoResult = geoVal.toFloat64();
  const uncertainty = geoVal.getUncertainty();
  const relUncertainty = geoVal.getRelativeUncertainty();

  return `<div class="geonum-result">
    <div class="geonum-label">${description}</div>
    <div class="geonum-comparison">
      <div class="geonum-ieee">IEEE 754: ${ieee.toExponential(8)}</div>
      <div class="geonum-geo">GeoNum: ${geoResult.toExponential(8)} ± ${uncertainty.toExponential(2)}</div>
      <div class="geonum-meta">Zone: ${geoVal.zone} | Shade: ${geoVal.shade} | Drift: ${geoVal.drift.toFixed(4)} | Rel. Unc: ${(relUncertainty * 100).toExponential(2)}%</div>
    </div>
  </div>`;
}

// --- 3D viewport adapter ---
// Map a fluids result set to a window.gdbs3D payload { points, values, title }.
// Each adapter targets the RICHEST genuine output the scan actually computed:
//  - LBM       -> the downsampled 2D velocity-magnitude field (one point per cell)
//                 plus the centerline profile revolved into a surface of revolution.
//  - Boundary  -> the published correlation surface Cf, delta, tau_w sampled densely
//                 over (Re, x), with the real computed configs marked on it.
//  - Pipe flow -> the Colebrook/Blasius friction-factor surface f(Re, eps/D), marked.
//  - Drag      -> the standard sphere drag curve Cd(Re) sampled densely, marked.
//  - Heat xfer -> the Dittus-Boelter / flat-plate Nu(Re, Pr) surface, marked.
//  - Compress. -> the isentropic + normal-shock ratio curves vs Mach, sampled densely.
// Geometry is per-axis normalized by the shared viewport, so the real units pass through.
// Guarded so nothing breaks when the shared viewport is absent.

const _MAX_3D_POINTS = 80000;

// Revolve a 1D profile (value vs radius/x) around the x axis into a tube/disk point cloud.
function revolveProfile(xs, vals, title) {
  const pts = [], values = [];
  const n = xs.length; if (!n) return null;
  const maxAbs = Math.max(1e-12, ...vals.map(v => Math.abs(v)));
  const segs = 36;
  for (let i = 0; i < n; i++) {
    const r = Math.max(0, Math.abs(vals[i]) / maxAbs);
    for (let s = 0; s < segs; s++) {
      const th = (s / segs) * 2 * Math.PI;
      pts.push(xs[i], r * Math.cos(th), r * Math.sin(th));
      values.push(vals[i]);
    }
  }
  return { points: new Float32Array(pts), values, title };
}

// Bar/scatter style result set: one point per config, x = index, z = value, coloured by value.
// Retained as a last-resort fallback for empty/degenerate results.
function scatterFromResults(r, valueKey, title) {
  const pts = [], values = [];
  for (let i = 0; i < r.length; i++) {
    const v = Number(r[i][valueKey]) || 0;
    pts.push(i, 0, v);
    values.push(v);
  }
  return { points: new Float32Array(pts), values, title };
}

// Turn a flat interleaved 2D vector field ([ny][nx][2] = ux,uy) into a coloured
// point cloud: one point per cell, z = |u|, coloured by speed.
function fieldToCloud(field, fnx, fny, title) {
  if (!field || !fnx || !fny) return null;
  const pts = [], values = [];
  const cells = fnx * fny;
  // Subsample stride if the grid is larger than the cap (it never is for LBM, but stay safe).
  const stride = Math.max(1, Math.ceil(cells / _MAX_3D_POINTS));
  let idx = 0;
  for (let j = 0; j < fny; j++) {
    for (let i = 0; i < fnx; i++, idx++) {
      if (idx % stride !== 0) continue;
      const ux = field[(j * fnx + i) * 2] || 0;
      const uy = field[(j * fnx + i) * 2 + 1] || 0;
      const spd = Math.sqrt(ux * ux + uy * uy);
      pts.push(i, j, spd);
      values.push(spd);
    }
  }
  if (!pts.length) return null;
  return { points: new Float32Array(pts), values, title };
}

// Sample a single-variable model y = f(x) densely into a curve, then mark the real
// computed configs as a second band at the same y so the data sits on the model.
// xs/ys are the genuine per-config (x, y) pairs the engine returned.
function modelCurveWithMarks(xMin, xMax, fn, xs, ys, title, logX) {
  const pts = [], values = [];
  const N = 240;
  for (let i = 0; i < N; i++) {
    const t = i / (N - 1);
    const x = logX ? Math.exp(Math.log(xMin) + t * (Math.log(xMax) - Math.log(xMin)))
                   : xMin + t * (xMax - xMin);
    const y = fn(x);
    if (!isFinite(y)) continue;
    pts.push(x, y, 0);          // model surface band at seriesIndex 0
    values.push(y);
  }
  for (let i = 0; i < xs.length; i++) {
    pts.push(xs[i], ys[i], 1);  // computed configs at seriesIndex 1
    values.push(ys[i]);
  }
  return { points: new Float32Array(pts), values, title };
}

// Sample a two-variable model z = f(x, y) over a dense grid into a surface point cloud,
// marking the real computed configs at seriesIndex (their own z) on top.
function modelSurfaceWithMarks(xMin, xMax, yMin, yMax, fn, marks, title, logX, logY) {
  const pts = [], values = [];
  const N = 56; // 56x56 = 3136 surface points
  for (let a = 0; a < N; a++) {
    const ta = a / (N - 1);
    const x = logX ? Math.exp(Math.log(xMin) + ta * (Math.log(xMax) - Math.log(xMin)))
                   : xMin + ta * (xMax - xMin);
    for (let b = 0; b < N; b++) {
      const tb = b / (N - 1);
      const y = logY ? Math.exp(Math.log(yMin) + tb * (Math.log(yMax) - Math.log(yMin)))
                     : yMin + tb * (yMax - yMin);
      const z = fn(x, y);
      if (!isFinite(z)) continue;
      pts.push(x, y, z);
      values.push(z);
    }
  }
  // marks: array of [x, y, z]
  for (const m of marks) { pts.push(m[0], m[1], m[2]); values.push(m[2]); }
  return { points: new Float32Array(pts), values, title };
}

function safeSpan(arr, padFrac, floor) {
  const fin = arr.filter(v => isFinite(v) && v !== 0);
  if (!fin.length) return [floor || 1, (floor || 1) * 10];
  let lo = Math.min(...fin), hi = Math.max(...fin);
  if (lo === hi) { lo = lo * 0.5; hi = hi * 1.5; }
  const pad = (hi - lo) * (padFrac || 0.15);
  return [Math.max(floor != null ? floor : -Infinity, lo - pad), hi + pad];
}

function publish3D(payload) {
  if (payload) window.__gdbs3dPayload = payload;
}

// Add a small "View in 3D" button near the results status line, idempotent.
function add3DButton(panelId, getPayload) {
  if (!window.gdbs3D) return;
  const panel = document.getElementById(panelId);
  if (!panel) return;
  let btn = panel.querySelector('.fl-view3d-btn');
  if (!btn) {
    btn = document.createElement('button');
    btn.className = 'fl-view3d-btn';
    btn.type = 'button';
    btn.textContent = 'View in 3D';
    btn.style.cssText = 'margin:8px 0;padding:6px 14px;background:#0b2030;color:#4fc3f7;border:1px solid #1e3a5f;border-radius:4px;cursor:pointer;font-size:12px;';
    panel.appendChild(btn);
  }
  btn.onclick = () => {
    const p = getPayload();
    publish3D(p);
    if (window.gdbs3D && window.gdbs3D.open && window.__gdbs3dPayload) window.gdbs3D.open(window.__gdbs3dPayload);
  };
}

// --- Scans ---

function boundaryScan() {
  status('boundary', 'Computing...'); const cfgs = JSON.parse(document.getElementById('in-boundary').value);
  const res = JSON.parse(run_boundary_scan(JSON.stringify({ configs: cfgs, precision: 0 })));
  const r = res.results;
  renderTable('boundary', ['Name', 'Re', 'Regime', '\u03B4 (mm)', 'Cf', '\u03C4_w (Pa)', 'x_tr (m)', 'Coherence'], r.map(x => [x.name, x.reynolds, x.regime, x.delta_mm, x.cf, x.wall_shear, x.transition_x, renderGradientBar(x.tiered_coherence)]));
  makeChart('boundary', r.map(x => x.name), [{ label: 'Re', data: r.map(x => x.reynolds), backgroundColor: '#80deea' }], 'Config', 'Reynolds Number');
  renderBoundaryRefNote('fluids-ref-boundary');

  // GeoNum precision demo using first result's key values
  if (r.length > 0) {
    const first = r[0];
    const geoDemo = geoNumDemo(first.reynolds, 'Reynolds Number')
      + geoNumDemo(first.cf, 'Skin Friction Coefficient')
      + geoNumDemo(first.wall_shear, 'Wall Shear Stress (Pa)');
    const demoEl = document.getElementById('geonum-demo-boundary');
    if (demoEl) demoEl.innerHTML = '<h4>GeoNum Precision Tracking</h4>' + geoDemo;
  }

  status('boundary', `Done — ${r.length} results`);
  animData.boundary = { results: r, mach: 0.3, reynolds: r[0]?.reynolds || 1e6, friction_coeff: r[0]?.cf || 0.003 };
  if (window.addAnimationButton) window.addAnimationButton('flp-boundary', 'fluids', () => animData.boundary, 'Boundary Layer Flow');
  // 3D: the published flat-plate skin-friction correlation surface Cf(Re) sampled
  // densely (laminar Blasius 0.664/sqrt(Re) below Re_tr, turbulent 0.0592/Re^0.2 above),
  // with the real computed configs marked on the curve. ~240 model points + marks.
  const make3D = () => {
    const xs = r.map(x => Number(x.reynolds) || 0);
    const ys = r.map(x => Number(x.cf) || 0);
    const [reLo, reHi] = safeSpan(xs, 0.2, 1e3);
    const cfModel = (re) => (re < 5e5 ? 0.664 / Math.sqrt(re) : 0.0592 / Math.pow(re, 0.2));
    return modelCurveWithMarks(reLo, reHi, cfModel, xs, ys,
      'Boundary Layer - flat-plate Cf(Re)', true);
  };
  publish3D(make3D());
  add3DButton('flp-boundary', make3D);
}

function pipeflowScan() {
  status('pipeflow', 'Computing...'); const pipes = JSON.parse(document.getElementById('in-pipeflow').value);
  const res = JSON.parse(run_pipeflow_scan(JSON.stringify({ pipes, precision: 0 })));
  const r = res.results;
  renderTable('pipeflow', ['Name', 'Re', 'Regime', 'f', '\u0394P (Pa)', 'Q (m\u00B3/s)', 'Coherence'], r.map(x => [x.name, x.reynolds, x.regime, x.friction_factor, x.pressure_drop, x.flow_rate, renderGradientBar(x.tiered_coherence)]));
  makeChart('pipeflow', r.map(x => x.name), [
    { label: 'Friction Factor \u00D7100', data: r.map(x => x.friction_factor * 100), backgroundColor: '#80deea' },
  ], 'Pipe', 'f \u00D7 100');
  renderPipeflowRefNote('fluids-ref-pipeflow');
  status('pipeflow', `Done — ${r.length} pipes`);
  animData.pipeflow = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('flp-pipeflow', 'fluids', () => animData.pipeflow, 'Pipe Flow');
  // 3D: the Colebrook-White friction-factor surface f(Re, eps/D) sampled densely
  // (Moody chart), with the real computed pipes marked at their (Re, eps/D, f).
  // ~3136 surface points + one mark per pipe.
  const make3D = () => {
    const relRough = pipes.map(p => (Number(p.roughness_mm) || 0) / 1000 / (Number(p.diameter_m) || 1));
    const reAll = r.map(x => Number(x.reynolds) || 0);
    const [reLo, reHi] = safeSpan(reAll, 0.2, 2300);
    // Colebrook-White solved iteratively; laminar branch f = 64/Re below transition.
    const fModel = (re, er) => {
      if (re < 2300) return 64 / re;
      let inv = 8; // 1/sqrt(f) initial guess
      for (let k = 0; k < 40; k++) {
        inv = -2 * Math.log10(er / 3.7 + 2.51 * inv / re);
      }
      return 1 / (inv * inv);
    };
    let erLo = Math.min(...relRough.filter(v => isFinite(v))), erHi = Math.max(...relRough.filter(v => isFinite(v)));
    if (!isFinite(erLo)) erLo = 0; if (!isFinite(erHi)) erHi = 0;
    erLo = Math.max(1e-6, erLo * 0.5); erHi = Math.max(erLo * 4, erHi * 1.5, 5e-2);
    const marks = r.map((x, i) => [Number(x.reynolds) || 0, relRough[i] || 1e-6, Number(x.friction_factor) || 0]);
    return modelSurfaceWithMarks(reLo, reHi, erLo, erHi, fModel, marks,
      'Pipe Flow - Moody f(Re, eps/D)', true, true);
  };
  publish3D(make3D());
  add3DButton('flp-pipeflow', make3D);
}

function dragScan() {
  status('drag', 'Computing...'); const bodies = JSON.parse(document.getElementById('in-drag').value);
  const res = JSON.parse(run_drag_scan(JSON.stringify({ bodies, precision: 0 })));
  const r = res.results;
  renderTable('drag', ['Name', 'Re', 'C_D', 'F_D (N)', 'V_t (m/s)', 'Mach', 'Coherence'], r.map(x => [x.name, x.reynolds, x.cd, x.drag_force, x.terminal_velocity, x.mach, renderGradientBar(x.tiered_coherence)]));
  makeChart('drag', r.map(x => x.name), [{ label: 'C_D', data: r.map(x => x.cd), backgroundColor: '#80deea' }], 'Body', 'Drag Coefficient');
  renderDragRefNote('fluids-ref-drag');
  status('drag', `Done — ${r.length} bodies`);
  animData.drag = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('flp-drag', 'fluids', () => animData.drag, 'Drag Analysis');
  // 3D: the standard sphere drag curve Cd(Re) (Morrison correlation, valid Re < 1e6)
  // sampled densely across the configs' Reynolds span, with the real bodies marked.
  const make3D = () => {
    const xs = r.map(x => Number(x.reynolds) || 0);
    const ys = r.map(x => Number(x.cd) || 0);
    const [reLo, reHi] = safeSpan(xs, 0.2, 1e-1);
    // Morrison (2013) smooth-sphere drag correlation.
    const cdModel = (re) => {
      if (re <= 0) return 0;
      return 24 / re
        + (2.6 * (re / 5.0)) / (1 + Math.pow(re / 5.0, 1.52))
        + (0.411 * Math.pow(re / 2.63e5, -7.94)) / (1 + Math.pow(re / 2.63e5, -8.0))
        + (0.25 * (re / 1e6)) / (1 + (re / 1e6));
    };
    return modelCurveWithMarks(reLo, reHi, cdModel, xs, ys, 'Drag - sphere Cd(Re)', true);
  };
  publish3D(make3D());
  add3DButton('flp-drag', make3D);
}

function heattransferScan() {
  status('heattransfer', 'Computing...'); const cfgs = JSON.parse(document.getElementById('in-heattransfer').value);
  const res = JSON.parse(run_heattransfer_scan(JSON.stringify({ configs: cfgs, precision: 0 })));
  const r = res.results;
  renderTable('heattransfer', ['Name', 'Re', 'Nu', 'h (W/m\u00B2K)', 'q (W/m\u00B2)', 'St', 'Coherence'], r.map(x => [x.name, x.reynolds, x.nusselt, x.h_coeff, x.heat_flux, x.stanton, renderGradientBar(x.tiered_coherence)]));
  makeChart('heattransfer', r.map(x => x.name), [{ label: 'Nusselt', data: r.map(x => x.nusselt), backgroundColor: '#80deea' }], 'Config', 'Nusselt Number');
  renderHeatTransferRefNote('fluids-ref-heattransfer');
  status('heattransfer', `Done — ${r.length} results`);
  animData.heattransfer = { results: r };
  if (window.addAnimationButton) window.addAnimationButton('flp-heattransfer', 'fluids', () => animData.heattransfer, 'Heat Transfer');
  // 3D: the Dittus-Boelter correlation surface Nu(Re, Pr) = 0.023 Re^0.8 Pr^0.4
  // sampled densely, with the real configs marked. Each config's effective Pr is
  // recovered from its own returned (Re, Nu) so the mark sits on the surface.
  const make3D = () => {
    const reAll = r.map(x => Number(x.reynolds) || 0);
    const prAll = r.map(x => {
      const re = Number(x.reynolds) || 0, nu = Number(x.nusselt) || 0;
      if (re <= 0 || nu <= 0) return 0.71;
      const pr = Math.pow(nu / (0.023 * Math.pow(re, 0.8)), 1 / 0.4);
      return isFinite(pr) && pr > 0 ? pr : 0.71;
    });
    const [reLo, reHi] = safeSpan(reAll, 0.2, 1e3);
    let prLo = Math.min(...prAll), prHi = Math.max(...prAll);
    if (!isFinite(prLo) || prLo <= 0) prLo = 0.6;
    if (!isFinite(prHi) || prHi <= 0) prHi = 7;
    prLo = Math.max(0.3, prLo * 0.7); prHi = Math.max(prLo * 3, prHi * 1.3);
    const nuModel = (re, pr) => 0.023 * Math.pow(re, 0.8) * Math.pow(pr, 0.4);
    const marks = r.map((x, i) => [Number(x.reynolds) || 0, prAll[i], Number(x.nusselt) || 0]);
    return modelSurfaceWithMarks(reLo, reHi, prLo, prHi, nuModel, marks,
      'Heat Transfer - Dittus-Boelter Nu(Re, Pr)', true, true);
  };
  publish3D(make3D());
  add3DButton('flp-heattransfer', make3D);
}

function compressibleScan() {
  status('compressible', 'Computing...'); const cfgs = JSON.parse(document.getElementById('in-compressible').value);
  const res = JSON.parse(run_compressible_scan(JSON.stringify({ configs: cfgs, precision: 0 })));
  const r = res.results;
  renderTable('compressible', ['Name', 'Mach', 'T\u2080/T', 'P\u2080/P', '\u03C1\u2080/\u03C1', 'M\u2082 (shock)', 'P\u2082/P\u2081', 'Coherence'], r.map(x => [x.name, x.mach, x.t0_t, x.p0_p, x.rho0_rho, x.shock_m2, x.shock_p2_p1, renderGradientBar(x.tiered_coherence)]));
  makeChart('compressible', r.map(x => x.name), [
    { label: 'P\u2080/P', data: r.map(x => x.p0_p), backgroundColor: '#80deea' },
    { label: 'T\u2080/T', data: r.map(x => x.t0_t), backgroundColor: '#4dd0e1' },
  ], 'Config', 'Ratio');
  status('compressible', `Done — ${r.length} results`);
  animData.compressible = { results: r, mach: r[0]?.mach || 2.0, reynolds: 1e6, friction_coeff: 0.003 };
  if (window.addAnimationButton) window.addAnimationButton('flp-compressible', 'fluids', () => animData.compressible, 'Compressible Flow');
  // 3D: the isentropic + normal-shock relations sampled densely vs Mach as several
  // curves (T0/T, P0/P, rho0/rho, normal-shock M2 and P2/P1), each at its own series
  // index (z), with the real computed configs marked. ~5 curves x 200 + marks.
  const make3D = () => {
    const machAll = r.map(x => Number(x.mach) || 0);
    const gamma = (cfgs && cfgs[0] && Number(cfgs[0].gamma)) || 1.4;
    const [mLo0, mHi] = safeSpan(machAll, 0.2, 0.05);
    const mLo = Math.max(0.05, mLo0);
    const t0t = (m) => 1 + (gamma - 1) / 2 * m * m;
    const p0p = (m) => Math.pow(t0t(m), gamma / (gamma - 1));
    const r0r = (m) => Math.pow(t0t(m), 1 / (gamma - 1));
    const shockM2 = (m) => {
      if (m <= 1) return 0;
      const num = 1 + (gamma - 1) / 2 * m * m;
      const den = gamma * m * m - (gamma - 1) / 2;
      return Math.sqrt(num / den);
    };
    const shockP = (m) => (m <= 1 ? 1 : 1 + 2 * gamma / (gamma + 1) * (m * m - 1));
    const series = [
      { z: 0, fn: t0t, key: 't0_t' },
      { z: 1, fn: p0p, key: 'p0_p' },
      { z: 2, fn: r0r, key: 'rho0_rho' },
      { z: 3, fn: shockM2, key: 'shock_m2' },
      { z: 4, fn: shockP, key: 'shock_p2_p1' },
    ];
    const pts = [], values = [];
    const N = 200;
    for (const s of series) {
      for (let i = 0; i < N; i++) {
        const m = mLo + (i / (N - 1)) * (mHi - mLo);
        const v = s.fn(m);
        if (!isFinite(v)) continue;
        pts.push(m, v, s.z);
        values.push(v);
      }
      // mark the real computed configs on this curve
      for (const x of r) {
        const m = Number(x.mach) || 0, v = Number(x[s.key]) || 0;
        pts.push(m, v, s.z + 0.5);
        values.push(v);
      }
    }
    return { points: new Float32Array(pts), values, title: 'Compressible - isentropic + shock relations vs Mach' };
  };
  publish3D(make3D());
  add3DButton('flp-compressible', make3D);
}

// ─── Lattice Boltzmann ──────────────────────────────────────────────────

const LBM_REFS = {
  taylor_green: {
    formula: 'u(x,y,t) = -U\u2080 cos(kx) sin(ky) e^{-2\u03BDk\u00B2t}',
    source: 'Taylor & Green 1937',
    note: 'Analytical decaying vortex — tests viscous dissipation accuracy'
  },
  poiseuille: {
    formula: 'u(y) = u_max (1 - (2y/H - 1)\u00B2)',
    source: 'Hagen-Poiseuille law',
    note: 'Parabolic channel flow — tests body-force and wall BC'
  },
  lid_cavity: {
    formula: 'Ghia, Ghia & Shin, J. Comput. Phys. 48 (1982) 387-411',
    source: 'Ghia et al. 1982',
    note: 'Standard benchmark for recirculating flow at Re = 100-10000'
  },
  method: {
    formula: 'f_i(x+e_i,t+1) = f_i(x,t) - (f_i - f_i^{eq}) / \u03C4',
    source: 'Kr\u00FCger et al., Springer 2017',
    note: 'D2Q9 lattice, BGK collision operator, \u03BD = (1/3)(\u03C4 - 0.5)'
  }
};

function renderLbmRefNote(containerId) {
  const el = document.getElementById(containerId);
  if (!el) return;
  el.innerHTML = `<div style="margin-top:10px;padding:8px 12px;background:#0b1520;border:1px solid #1e3a5f;border-radius:4px;font-size:11px;color:#94a3b8;line-height:1.7;">
    <strong style="color:#4fc3f7;">Lattice Boltzmann Method - D2Q9</strong><br>
    BGK collision: <strong>${LBM_REFS.method.formula}</strong> (${LBM_REFS.method.source})<br>
    Taylor-Green vortex: ${LBM_REFS.taylor_green.note}<br>
    Poiseuille flow: ${LBM_REFS.poiseuille.note}<br>
    Lid-driven cavity: ${LBM_REFS.lid_cavity.note} (${LBM_REFS.lid_cavity.source})
  </div>`;
}

async function lbmScan() {
  status('lbm', 'Running LBM simulation...');
  showLoading('Computing...', 'Running Lattice Boltzmann D2Q9 simulation');
  // Yield one frame so the spinner paints before the synchronous WASM call blocks the main thread.
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
  const cfgs = JSON.parse(document.getElementById('in-lbm').value);
  const res = JSON.parse(run_lbm_scan(JSON.stringify({ configs: cfgs, precision: 0, n_conformers: 64 })));
  const r = res.results;

  // Summary table
  renderTable('lbm', ['Name', 'Benchmark', 'Grid', 'Re', '\u03C4', '\u03BD', 'Steps', 'Converged', 'L\u2082 Error', 'Max |u|', 'Coherence'],
    r.map(x => [x.name, x.benchmark, `${x.nx}\u00D7${x.ny}`, fmtSci(x.reynolds), x.tau.toFixed(3),
      fmtSci(x.nu_lattice), x.total_steps, x.converged ? '\u2714' : '\u2718', fmtSci(x.final_l2_error),
      fmtSci(x.final_max_velocity), renderGradientBar(x.tiered_coherence)]));

  // Bar chart: L2 error per config
  makeChart('lbm', r.map(x => x.name), [
    { label: 'L\u2082 Error', data: r.map(x => x.final_l2_error), backgroundColor: '#4fc3f7' },
    { label: 'Max |u|', data: r.map(x => x.final_max_velocity), backgroundColor: '#80deea' },
  ], 'Config', 'Value');

  // Centerline profile chart (first result)
  if (r.length > 0 && r[0].centerline_ux.length > 0) {
    const first = r[0];
    makeChart('lbm-profile',
      first.centerline_y.map(y => y.toFixed(3)),
      [{ label: `u_x centerline (${first.benchmark})`, data: first.centerline_ux, borderColor: '#4fc3f7', backgroundColor: 'rgba(79,195,247,0.15)', fill: true, pointRadius: 0 }],
      'y / L', 'u_x', 'line');
  }

  // Kinetic energy decay chart (first result with snapshots)
  if (r.length > 0 && r[0].snapshots.length > 1) {
    const snaps = r[0].snapshots;
    makeChart('lbm-energy',
      snaps.map(s => s.step.toString()),
      [
        { label: 'Kinetic Energy', data: snaps.map(s => s.kinetic_energy), borderColor: '#4fc3f7', backgroundColor: 'transparent', pointRadius: 2 },
        { label: 'Enstrophy', data: snaps.map(s => s.enstrophy), borderColor: '#ff8a65', backgroundColor: 'transparent', pointRadius: 2 },
      ],
      'Step', 'Energy (per node)', 'line');
  }

  renderLbmRefNote('fluids-ref-lbm');

  // GeoNum precision demo
  if (r.length > 0) {
    const first = r[0];
    const geoDemo = geoNumDemo(first.reynolds, 'Reynolds Number')
      + geoNumDemo(first.nu_lattice, 'Lattice Viscosity \u03BD')
      + geoNumDemo(first.final_kinetic_energy, 'Final Kinetic Energy');
    const demoEl = document.getElementById('geonum-demo-lbm');
    if (demoEl) demoEl.innerHTML = '<h4>GeoNum Precision Tracking</h4>' + geoDemo;
  }

  status('lbm', `Done - ${r.length} simulations`);
  animData.lbm = { results: r, type: 'lbm' };
  if (window.addAnimationButton) window.addAnimationButton('flp-lbm', 'lbm', () => animData.lbm, 'Lattice Boltzmann Flow');
  // 3D: the genuine downsampled 2D velocity-magnitude field from the final snapshot
  // (one point per grid cell, z = |u|, coloured by speed) - the richest LBM output.
  // Falls back to the revolved centerline profile, then to an L2-error scatter.
  const make3D = () => {
    const f = r[0];
    if (f && f.snapshots && f.snapshots.length) {
      const snap = f.snapshots[f.snapshots.length - 1];
      if (snap && snap.velocity_field && snap.velocity_field.length && snap.field_nx && snap.field_ny) {
        const cloud = fieldToCloud(snap.velocity_field, snap.field_nx, snap.field_ny,
          `LBM - velocity field |u| (${f.benchmark})`);
        if (cloud) return cloud;
      }
    }
    if (f && f.centerline_ux && f.centerline_ux.length > 1 && f.centerline_y) {
      return revolveProfile(f.centerline_y, f.centerline_ux, `LBM - u_x centerline (${f.benchmark})`);
    }
    return scatterFromResults(r, 'final_l2_error', 'LBM - L2 Error');
  };
  publish3D(make3D());
  add3DButton('flp-lbm', make3D);
  } catch (err) {
    console.error('LBM scan failed:', err);
    status('lbm', `Error - ${err && err.message ? err.message : err}`);
  } finally {
    hideLoading();
  }
}
